// VMT Reduction Component Types

export interface VmtReductionComponents {
  transitOrientedDevelopment: number; // 0-2% (annual housing growth rate; same as Compact Development detail slider)
  activeTransportation: number; // 0-10%
  transitImprovements: number; // 0-10% (VMT reduction %)
}

// Energy Efficiency Component Types (multi-policy; stock model in buildingsEfficiencyModel)
export interface EnergyEfficiencyComponents {
  /** New residential only: % intensity reduction vs all-electric baseline by 2045 (linear ramp). */
  residentialReachCodePercent: number; // 0-40
  /** Existing residential: % demand reduction by 2045 (linear ramp). */
  residentialRetrofitExistingPercent: number; // 0-50
  /** Existing commercial: % demand reduction by 2045 (linear ramp). */
  commercialRetrofitExistingPercent: number; // 0-50
  /** Residual lever; collapsed headline solves this vs named policies */
  otherEfficiencyPercent: number; // 0-40
}

function clampEff(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Coerce saved/URL payloads to the current shape (maps legacy weatherization → residential retrofit;
 * drops commercial reach & demand response).
 */
export function normalizeEnergyEfficiencyComponents(
  raw: Partial<EnergyEfficiencyComponents> | null | undefined
): EnergyEfficiencyComponents {
  const def = {
    residentialReachCodePercent: 0,
    residentialRetrofitExistingPercent: 0,
    commercialRetrofitExistingPercent: 0,
    otherEfficiencyPercent: 0,
  };
  if (!raw || typeof raw !== 'object') return def;
  const r = raw as Record<string, unknown>;

  let resRetro = Number(r.residentialRetrofitExistingPercent);
  let comRetro = Number(r.commercialRetrofitExistingPercent);
  if (!Number.isFinite(resRetro)) resRetro = 0;
  if (!Number.isFinite(comRetro)) comRetro = 0;

  if (
    r.weatherizationPercent != null &&
    r.residentialRetrofitExistingPercent == null &&
    r.commercialRetrofitExistingPercent == null
  ) {
    resRetro = clampEff(Number(r.weatherizationPercent), 0, 50);
  } else {
    resRetro = clampEff(resRetro, 0, 50);
    comRetro = clampEff(comRetro, 0, 50);
  }

  return {
    residentialReachCodePercent: clampEff(Number(r.residentialReachCodePercent ?? 0), 0, 40),
    residentialRetrofitExistingPercent: resRetro,
    commercialRetrofitExistingPercent: comRetro,
    otherEfficiencyPercent: clampEff(Number(r.otherEfficiencyPercent ?? 0), 0, 40),
  };
}

/** Default allocation when distributing a collapsed headline across named levers. */
export const DEFAULT_ENERGY_EFFICIENCY_DISTRIBUTION = {
  residentialRetrofitExistingPercent: 0.5,
  residentialReachCodePercent: 0.2,
  commercialRetrofitExistingPercent: 0.15,
};

export function getDefaultEnergyEfficiencyComponents(): EnergyEfficiencyComponents {
  return normalizeEnergyEfficiencyComponents(null);
}

/** True if any efficiency component has non-zero reduction. */
export function hasAnyEfficiencyComponent(c: EnergyEfficiencyComponents | undefined | null): boolean {
  if (!c) return false;
  const n = normalizeEnergyEfficiencyComponents(c);
  return (
    (n.residentialReachCodePercent ?? 0) > 0 ||
    (n.residentialRetrofitExistingPercent ?? 0) > 0 ||
    (n.commercialRetrofitExistingPercent ?? 0) > 0 ||
    (n.otherEfficiencyPercent ?? 0) > 0
  );
}

/** Distribute a target % to components (fallback when headline > 0 but no components). */
export function distributeEnergyEfficiencyTarget(
  targetPercent: number,
  _currentComponents: EnergyEfficiencyComponents
): EnergyEfficiencyComponents {
  const d = DEFAULT_ENERGY_EFFICIENCY_DISTRIBUTION;
  const resRetro = Math.min(50, targetPercent * d.residentialRetrofitExistingPercent);
  const resReach = Math.min(40, targetPercent * d.residentialReachCodePercent);
  const comRetro = Math.min(50, targetPercent * d.commercialRetrofitExistingPercent);
  const other = Math.max(0, targetPercent - resRetro - resReach - comRetro);
  return normalizeEnergyEfficiencyComponents({
    residentialReachCodePercent: resReach,
    residentialRetrofitExistingPercent: resRetro,
    commercialRetrofitExistingPercent: comRetro,
    otherEfficiencyPercent: other,
  });
}

export interface ParkingPricingAssumptions {
  priceIncreasePercent: number; // 10-300% (main lever)
  vmtAffectedPercent: number; // Percentage of total VMT that can be affected (default 25%)
  startingPricePerHour: number; // Starting parking price per hour (default $0)
  commuteByCarPercent: number; // Percentage who commute by car alone (fetched from database)
}

export interface InterventionAreas {
  // Transportation (Light Blue)
  urbanInfill: number; // 0-100% by 2045
  vmtReduction: number; // 0-20% by 2045
  /**
   * Beyond-BAU ZEV ambition (0–100).
   * 0 = EMFAC BAU fleet EV share in 2045; 100 = 100% light-duty ZEV by 2045.
   * Mapped to absolute `evAdoptionPercent` in the policy model.
   */
  vehicleElectrification: number;
  // Buildings (Yellow)
  energyEfficiency: number; // 0-100% by 2045
  buildingsElectrification: number; // 0-100% by 2045
  gridDecarbonization: number; // year electricity reaches zero emissions (2025-2045)
  // Other (Orange)
  /**
   * Additional organics diversion beyond BAU by 2045 (percentage points, 0–50).
   * Modeling intent (simple first draft): reduce waste-sector emissions proportionally to avoided organics-to-landfill.
   * IMPORTANT: BAU already includes statewide organics policies; this lever represents LOCAL measures above BAU.
   */
  organicsDiversion: number; // 0-50 (% points additional by 2045)
}

export interface VmtComponentMetadata {
  key: keyof VmtReductionComponents;
  label: string;
  description: string;
  maxPercent: number;
  color: string;
  literatureSource: string;
}

// Component metadata with colors (shades of green to match existing VMT green #10b981)
export const VMT_COMPONENT_METADATA: VmtComponentMetadata[] = [
  {
    key: 'transitOrientedDevelopment',
    label: 'Compact Development',
    description: 'Percent growth per year (0–2%): annual policy housing growth rate; shifts growth into lower-VMT cities; worksheet models out-of-boundary and net statewide savings',
    maxPercent: 2,
    color: '#059669', // Green-600
    literatureSource: 'Compact development elasticity; density effect (-0.22)',
  },
  {
    key: 'activeTransportation',
    label: 'Active Transportation Infrastructure',
    description: 'Build protected bike lanes, pedestrian infrastructure, and complete streets',
    maxPercent: 10,
    color: '#10b981', // Green-500 (main VMT color)
    literatureSource: 'CARB Technical Guidance on Bicycle Facilities',
  },
  {
    key: 'transitImprovements',
    label: 'Transit Service Improvements',
    description: 'Expand transit frequency, coverage, and provide fare subsidies',
    maxPercent: 10,
    color: '#6ee7b7', // Green-300
    literatureSource: 'Transit proximity: 25-50% VMT reduction',
  },
];

// Default distribution ratios (used when scaling from total slider)
export const DEFAULT_VMT_DISTRIBUTION = {
  transitOrientedDevelopment: 0.5, // 50% of total
  activeTransportation: 0.2, // 20% of total
  transitImprovements: 0.3, // 30% of total
};

// Helper function to get default (zero) components
export function getDefaultVmtComponents(): VmtReductionComponents {
  return {
    transitOrientedDevelopment: 0,
    activeTransportation: 0,
    transitImprovements: 0,
  };
}

// Helper function to get default (zero) intervention areas
export function getDefaultInterventionAreas(): InterventionAreas {
  return {
    urbanInfill: 0,
    vmtReduction: 0,
    vehicleElectrification: 0,
    energyEfficiency: 0,
    buildingsElectrification: 0,
    // Default clean electricity year per SB 100.
    gridDecarbonization: 2045,
    // Default 0 = no additional local diversion beyond BAU.
    organicsDiversion: 0,
  };
}

// Calculate total VMT reduction from components
export function calculateTotalVmtReduction(components: VmtReductionComponents): number {
  return (
    components.transitOrientedDevelopment +
    components.activeTransportation +
    components.transitImprovements
  );
}

// Distribute total VMT reduction across components proportionally
export function distributeVmtReduction(
  totalPercent: number,
  currentComponents: VmtReductionComponents
): VmtReductionComponents {
  const currentTotal = calculateTotalVmtReduction(currentComponents);

  if (currentTotal === 0) {
    // Use default distribution based on literature-supported ratios
    return {
      transitOrientedDevelopment: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.transitOrientedDevelopment, 2),
      activeTransportation: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.activeTransportation, 10),
      transitImprovements: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.transitImprovements, 10),
    };
  }

  // Proportional scaling
  const scaleFactor = totalPercent / currentTotal;
  return {
    transitOrientedDevelopment: Math.min(currentComponents.transitOrientedDevelopment * scaleFactor, 2),
    activeTransportation: Math.min(currentComponents.activeTransportation * scaleFactor, 10),
    transitImprovements: Math.min(currentComponents.transitImprovements * scaleFactor, 10),
  };
}

// Validate VMT components
export function validateVmtComponents(
  components: VmtReductionComponents
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (components.transitOrientedDevelopment > 2) {
    errors.push('Compact Development exceeds max (2%)');
  }
  if (components.activeTransportation > 10) {
    errors.push('Active transport exceeds max (10%)');
  }
  if (components.transitImprovements > 10) {
    errors.push('Transit improvements exceeds max (10%)');
  }

  const total = calculateTotalVmtReduction(components);
  if (total > 50) {
    errors.push('Total VMT reduction exceeds 50%');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Micromobility assumptions used by the Policy Impact Tool
export interface MicromobilityAssumptions {
  yearImplemented: number; // default 2025
  tripsPerResident: number; // e.g., 3 trips per resident per year
  percentBike: number; // percent of program trips by e-bike (0-100)
  percentScooter: number; // percent of program trips by e-scooter (0-100)
  milesPerBikeTrip: number; // average miles per e-bike trip
  milesPerScooterTrip: number; // average miles per e-scooter trip
  percentBikeTripsDisplaceCars: number; // 0-100
  percentScooterTripsDisplaceCars: number; // 0-100
  kwhPerMileBike: number; // EV bike electricity consumption (kWh/mile)
  kwhPerMileScooter: number; // EV scooter electricity consumption (kWh/mile)
  scalePercent: number; // user scale control (0-200%)
}
